import {
  Avatar,
  Badge,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Select,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import Paginator from '../../../components/paginator'
import { formatDateTime } from '../../../helpers'
import { TeacherService } from '../../../services/Teacher/TeacherService'
import { Teacher, TeacherComplete, TeachingFormat } from '../../../services/Teacher/types'
import { TeacherDeleteDialog } from './TeacherDeleteDialog'
import { TeacherFormModal } from './TeacherFormModal'

const formats: Record<TeachingFormat, string> = {
  IN_PERSON: 'Presencial',
  LIVE_ONLINE: 'Online/Ao Vivo'
}

export default function TeacherPage() {
  const service = new TeacherService()
  const ref = useRef<HTMLButtonElement>(null)
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [targetRecord, setTargetRecord] = useState<Teacher>()
  const [filters, setFilters] = useState({ search: '', length: '10' })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [recordEditing, setRecordEditing] = useState<TeacherComplete>()

  useEffect(() => {
    refreshList()
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refreshList()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [filters, page])

  useEffect(() => {
    if (!isCreateModalOpen) {
      refreshList()
    }
  }, [isCreateModalOpen])

  const refreshList = async () => {
    service.list({ ...filters, page }).then((response) => {
      setTeachers(response.data)
      setTotalPages(Math.ceil(response.total_filtered / parseInt(filters.length)))
    })
  }

  const openEditModal = (id: number) => {
    service.get(id).then((response) => {
      setRecordEditing(response)
      setIsCreateModalOpen(true)
    })
  }

  return (
    <>
      <Heading as="h1" size="lg" marginBottom="5" mb="10" textTransform="uppercase">
        Professores
      </Heading>

      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setFilters({ ...filters, length: e.target.value })}
            value={filters.length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            value={filters.search}
          />
        </Flex>
      </Flex>

      <TableContainer fontSize="smaller" padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th p={1}>ID</Th>
              <Th p={1}>Nome</Th>
              <Th p={1}>Formatos</Th>
              <Th p={1}>Status</Th>
              <Th p={1}>Criado em</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {teachers.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  Nenhum professor cadastrado
                </Td>
              </Tr>
            ) : (
              teachers.map((teacher) => (
                <Tr key={teacher.id}>
                  <Td p={1}>{teacher.id}</Td>
                  <Td p={1}>
                    <Flex alignItems="center" gap={2}>
                      <Avatar size="sm" src={teacher.profile_picture_thumbnail} name={teacher.name} />
                      {teacher.name}
                    </Flex>
                  </Td>
                  <Td p={1}>{teacher.teaching_formats.map((format) => formats[format]).join(', ')}</Td>
                  <Td p={1}>
                    <Badge colorScheme={teacher.enabled ? 'green' : 'red'} fontSize="0.8em" px="2">
                      {teacher.enabled ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </Td>
                  <Td p={1}>{formatDateTime(teacher.created_at)}</Td>
                  <Td p={1}>
                    <Tooltip label="Editar" hasArrow placement="top">
                      <IconButton
                        aria-label="Editar"
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          openEditModal(teacher.id)
                        }}
                      >
                        <i className="lni lni-pencil"></i>
                      </IconButton>
                    </Tooltip>
                    <Tooltip label="Excluir" hasArrow placement="top">
                      <IconButton
                        aria-label="Excluir"
                        colorScheme="red"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setTargetRecord(teacher)
                          setOpenDeleteDialog(true)
                        }}
                      >
                        <i className="lni lni-trash-can"></i>
                      </IconButton>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      {targetRecord && (
        <TeacherDeleteDialog
          isOpen={openDeleteDialog}
          teacher={targetRecord}
          leastDestructiveRef={ref}
          onClose={() => {
            setOpenDeleteDialog(false)
            refreshList()
          }}
        />
      )}

      <Tooltip label="Adicionar professor" aria-label="Adicionar professor" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setRecordEditing(undefined)
            setIsCreateModalOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <TeacherFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        teacher={recordEditing}
      />
    </>
  )
}
